import React, { useState } from 'react';

import type { UserDTO } from '../../services/apiClient';

import Table from '../../components/common/Table';

interface UserTableProps {
  users: UserDTO[];
  userImages: { [key: string]: string };
  onEdit: (user: UserDTO) => void;
  onDelete: (user: UserDTO) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  isLoading: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  onSort?: (sortKey: string) => void;
}

const getUserInitials = (user: UserDTO): string => {
  const initials =
    `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}`.toUpperCase();

  return initials || '?';
};

// The picture, or the initials when there is none or it fails to load (a migrated record whose
// file never arrived), rather than the browser's broken image.
const UserAvatar: React.FC<{ user: UserDTO; url?: string }> = ({
  user,
  url,
}) => {
  const [failedUrl, setFailedUrl] = useState<string>();

  return url && url !== failedUrl ? (
    <img
      src={url}
      alt={`${user.firstName} ${user.lastName}`}
      className="rounded-full object-cover"
      style={{ width: '40px', height: '40px' }}
      onError={() => setFailedUrl(url)}
    />
  ) : (
    <div
      className="rounded-full bg-gray-300 text-white flex items-center justify-center"
      style={{ width: '40px', height: '40px' }}
    >
      {getUserInitials(user)}
    </div>
  );
};

const UserTable: React.FC<UserTableProps> = ({
  users,
  userImages,
  onEdit,
  onDelete,
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  isLoading,
  sortBy,
  sortDescending,
  onSort,
}) => (
  <Table
    columns={[
      {
        label: '',
        key: 'profilePictureFileId',
        isImage: true,
        imageSize: '40px',
        className: 'hidden sm:table-cell',
        render: (user) => (
          <UserAvatar
            user={user}
            url={
              user.profilePictureFileId && user.id
                ? userImages[user.id]
                : undefined
            }
          />
        ),
      },
      {
        label: 'First Name',
        key: 'firstName',
        sortable: true,
        sortKey: 'FirstName',
      },
      {
        label: 'Last Name',
        key: 'lastName',
        sortable: true,
        sortKey: 'LastName',
      },
      {
        label: 'Email',
        key: 'email',
        className: 'hidden sm:table-cell',
        sortable: true,
        sortKey: 'Email',
      },
    ]}
    data={users}
    actions={{
      onEdit: (user) => onEdit(user),
      onDelete: (user) => onDelete(user),
    }}
    currentPage={currentPage}
    totalPages={totalPages}
    onPageChange={onPageChange}
    pageSize={pageSize}
    onPageSizeChange={onPageSizeChange}
    isLoading={isLoading}
    sortBy={sortBy}
    sortDescending={sortDescending}
    onSort={onSort}
  />
);

export default UserTable;
